import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import './estilodash.css';

function toMoney(value) {
  return Number(value || 0).toFixed(2);
}

function getStatusValue(assinatura) {
  const raw = assinatura?.assinatura_status ?? assinatura?.status ?? 0;
  return Number(raw) === 1 ? 1 : 0;
}

function getTipoAssinatura(assinatura) {
  if (assinatura?.tipo) return assinatura.tipo;
  return assinatura?.produto_id ? 'individual' : 'pacote';
}

function getPrecoAssinatura(assinatura, produtos, pacotes) {
  if (!assinatura) return 0;

  const tier = String(assinatura.tier || 'starter');

  if (getTipoAssinatura(assinatura) === 'pacote') {
    const pacote = pacotes.find(
      (p) => Number(p.id) === Number(assinatura.pacote_id)
    );
    return Number(pacote?.precos?.[tier]?.valor_brl || 0);
  }

  const produto = produtos.find(
    (p) => Number(p.id) === Number(assinatura.produto_id)
  );
  return Number(produto?.precos?.[tier]?.valor_brl || 0);
}

function formatMesReferencia(mes) {
  if (!mes || !String(mes).includes('-')) return mes || '';
  const [ano, mesNum] = String(mes).split('-');
  return `${mesNum}/${ano}`;
}

function montarDescricaoAutomatica({
  clienteId,
  mesReferencia,
  assinaturaSelecionada,
  resumoCliente,
  produtos,
  pacotes,
}) {
  if (!clienteId) return '';

  const mesFmt = formatMesReferencia(mesReferencia);

  if (assinaturaSelecionada) {
    const valor = getPrecoAssinatura(assinaturaSelecionada, produtos, pacotes);
    const nome =
      assinaturaSelecionada.nome_plano ||
      assinaturaSelecionada.codigo_plano ||
      'Assinatura';
    const tipo =
      getTipoAssinatura(assinaturaSelecionada) === 'pacote'
        ? 'pacote'
        : 'individual';

    return `Mensalidade ${mesFmt} - ${nome} (${assinaturaSelecionada.tier}, ${tipo}) - R$ ${toMoney(valor)}`;
  }

  if (
    resumoCliente &&
    Array.isArray(resumoCliente.lista) &&
    resumoCliente.lista.length > 0
  ) {
    const itens = resumoCliente.lista.map((a) => {
      const nome = a.nome_plano || a.codigo_plano || 'Assinatura';
      const tipo = a.tipo_resumo === 'pacote' ? 'pacote' : 'individual';
      return `${nome} (${a.tier}, ${tipo}) - R$ ${toMoney(
        a.valor_calculado_front
      )}`;
    });

    return `Mensalidade ${mesFmt} - ${itens.join(' + ')}`;
  }

  return `Mensalidade ${mesFmt}`;
}

export default function AdicionaPagamentos() {
  const [clientes, setClientes] = useState([]);
  const [assinaturas, setAssinaturas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [pacotes, setPacotes] = useState([]);

  const [form, setForm] = useState({
    cliente_id: '',
    assinatura_id: '',
    mes_referencia: '',
    data_pagamento: '',
    valor: '',
    descricao: '',
    metodo_pagamento: 'PIX',
    status: 'Aguardando pagamento',
  });

  const [msg, setMsg] = useState('');
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const carregarDados = async () => {
      try {
        setLoading(true);
        setErro('');
        setMsg('');

        const [c, a, p, pac] = await Promise.all([
          api.get('/api/clientes'),
          api.get('/api/assinaturas'),
          api.get('/api/produtos-ativos'),
          api.get('/api/pacotes-ativos'),
        ]);

        setClientes(c.data || []);
        setAssinaturas(a.data || []);
        setProdutos(p.data || []);
        setPacotes(pac.data || []);

        const hoje = new Date();
        const yyyy = hoje.getFullYear();
        const mm = String(hoje.getMonth() + 1).padStart(2, '0');
        const dd = String(hoje.getDate()).padStart(2, '0');

        setForm((prev) => ({
          ...prev,
          data_pagamento: `${yyyy}-${mm}-${dd}`,
          mes_referencia: `${yyyy}-${mm}`,
        }));
      } catch (err) {
        console.error('Erro ao carregar dados de pagamentos:', err);
        setErro('Erro ao carregar clientes, assinaturas e catálogo.');
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, []);

  const assinaturasCliente = useMemo(() => {
    return assinaturas.filter(
      (a) =>
        String(a.cliente_id) === String(form.cliente_id) &&
        getStatusValue(a) === 1
    );
  }, [assinaturas, form.cliente_id]);

  const resumoCliente = useMemo(() => {
    if (!form.cliente_id) return null;

    const lista = assinaturasCliente.map((a) => {
      const valor = getPrecoAssinatura(a, produtos, pacotes);
      return {
        ...a,
        valor_calculado_front: valor,
        tipo_resumo: getTipoAssinatura(a),
      };
    });

    const total = lista.reduce(
      (acc, item) => acc + Number(item.valor_calculado_front || 0),
      0
    );

    const qtdIndividuais = lista.filter(
      (a) => a.tipo_resumo === 'individual'
    ).length;

    const qtdPacotes = lista.filter((a) => a.tipo_resumo === 'pacote').length;

    return {
      lista,
      total,
      qtdIndividuais,
      qtdPacotes,
    };
  }, [form.cliente_id, assinaturasCliente, produtos, pacotes]);

  const assinaturaSelecionada = useMemo(() => {
    return (
      assinaturasCliente.find(
        (a) => String(a.id) === String(form.assinatura_id)
      ) || null
    );
  }, [assinaturasCliente, form.assinatura_id]);

  useEffect(() => {
    if (!form.cliente_id) {
      setForm((prev) => ({
        ...prev,
        assinatura_id: '',
        valor: '',
        descricao: '',
      }));
      return;
    }

    const assinaturaAtual = form.assinatura_id
      ? assinaturasCliente.find(
          (a) => String(a.id) === String(form.assinatura_id)
        ) || null
      : null;

    const valorCalculado = assinaturaAtual
      ? getPrecoAssinatura(assinaturaAtual, produtos, pacotes)
      : Number(resumoCliente?.total || 0);

    const descricaoAutomatica = montarDescricaoAutomatica({
      clienteId: form.cliente_id,
      mesReferencia: form.mes_referencia,
      assinaturaSelecionada: assinaturaAtual,
      resumoCliente,
      produtos,
      pacotes,
    });

    setForm((prev) => {
      const descricaoAtual = String(prev.descricao || '');
      const podeSobrescrever =
        !descricaoAtual || descricaoAtual.startsWith('Mensalidade');

      return {
        ...prev,
        assinatura_id: assinaturaAtual ? prev.assinatura_id : '',
        valor: valorCalculado > 0 ? toMoney(valorCalculado) : '',
        descricao: podeSobrescrever ? descricaoAutomatica : prev.descricao,
      };
    });
  }, [
    form.cliente_id,
    form.assinatura_id,
    form.mes_referencia,
    assinaturasCliente,
    resumoCliente,
    produtos,
    pacotes,
  ]);

  const handleClienteChange = (value) => {
    setMsg('');
    setErro('');
    setForm((prev) => ({
      ...prev,
      cliente_id: value,
      assinatura_id: '',
      valor: '',
      descricao: '',
    }));
  };

  const handleAssinaturaChange = (value) => {
    setMsg('');
    setErro('');

    if (!value) {
      setForm((prev) => ({
        ...prev,
        assinatura_id: '',
        valor: resumoCliente ? toMoney(resumoCliente.total) : '',
        descricao: montarDescricaoAutomatica({
          clienteId: form.cliente_id,
          mesReferencia: form.mes_referencia,
          assinaturaSelecionada: null,
          resumoCliente,
          produtos,
          pacotes,
        }),
      }));
      return;
    }

    const assinatura = assinaturasCliente.find(
      (a) => String(a.id) === String(value)
    );

    const valorAss = assinatura
      ? getPrecoAssinatura(assinatura, produtos, pacotes)
      : 0;

    setForm((prev) => ({
      ...prev,
      assinatura_id: value,
      valor: valorAss > 0 ? toMoney(valorAss) : '',
      descricao: montarDescricaoAutomatica({
        clienteId: form.cliente_id,
        mesReferencia: form.mes_referencia,
        assinaturaSelecionada: assinatura,
        resumoCliente,
        produtos,
        pacotes,
      }),
    }));
  };

  const salvar = async (e) => {
    e.preventDefault();
    setMsg('');
    setErro('');

    if (!form.cliente_id) {
      setErro('Selecione o cliente.');
      return;
    }

    if (!form.data_pagamento) {
      setErro('Informe a data do pagamento.');
      return;
    }

    if (!form.mes_referencia) {
      setErro('Informe o mês de referência.');
      return;
    }

    const valorNumerico = Number(String(form.valor).replace(',', '.'));
    if (Number.isNaN(valorNumerico) || valorNumerico < 0) {
      setErro('Valor inválido.');
      return;
    }

    try {
      const body = {
        ...form,
        cliente_id: Number(form.cliente_id),
        assinatura_id: form.assinatura_id ? Number(form.assinatura_id) : null,
        origem_cobranca: form.assinatura_id ? 'assinatura' : 'cliente',
        valor: valorNumerico,
      };

      await api.post('/api/pagamentos', body);

      setMsg('Pagamento cadastrado com sucesso.');
      setErro('');

      setForm((prev) => ({
        ...prev,
        assinatura_id: '',
        valor: resumoCliente ? toMoney(resumoCliente.total) : '',
        descricao: resumoCliente
          ? montarDescricaoAutomatica({
              clienteId: form.cliente_id,
              mesReferencia: form.mes_referencia,
              assinaturaSelecionada: null,
              resumoCliente,
              produtos,
              pacotes,
            })
          : '',
      }));
    } catch (err) {
      console.error('Erro ao cadastrar pagamento:', err);
      setErro(err.response?.data?.message || 'Erro ao cadastrar pagamento.');
    }
  };

  return (
    <div className="pagamentos-container">
      <h2>Adicionar Pagamento</h2>
      <p>
        O valor é preenchido automaticamente conforme o plano/tier da assinatura.
        Se deixar <strong>Sem assinatura</strong>, o sistema soma as assinaturas
        ativas do cliente.
      </p>

      {msg && (
        <div
          style={{
            background: 'rgba(47,208,182,.14)',
            color: '#d9fff5',
            border: '1px solid #b7e4c7',
            borderRadius: 6,
            padding: 10,
            marginBottom: 12,
          }}
        >
          {msg}
        </div>
      )}

      {erro && (
        <div
          style={{
            background: 'rgba(255,107,129,.14)',
            color: '#ffdce2',
            border: '1px solid #f5b7b7',
            borderRadius: 6,
            padding: 10,
            marginBottom: 12,
          }}
        >
          {erro}
        </div>
      )}

      {loading ? (
        <p>Carregando dados...</p>
      ) : (
        <form onSubmit={salvar} className="qt-form-panel qt-admin-form qt-form-pagamento">
          <div className="form-linha" style={{ alignItems: 'stretch' }}>
            <label style={{ display: 'flex', flexDirection: 'column' }}>
              Cliente
              <select
                value={form.cliente_id}
                onChange={(e) => handleClienteChange(e.target.value)}
                style={{ minHeight: 42 }}
              >
                <option value="">Selecione</option>
                {clientes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.id} - {c.nome}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column' }}>
              Assinatura
              <select
                value={form.assinatura_id}
                onChange={(e) => handleAssinaturaChange(e.target.value)}
                disabled={!form.cliente_id}
                style={{ minHeight: 42 }}
              >
                <option value="">Sem assinatura (somar do cliente)</option>
                {assinaturasCliente.map((a) => {
                  const valorPrevio = getPrecoAssinatura(a, produtos, pacotes);
                  return (
                    <option key={a.id} value={a.id}>
                      {a.nome_plano || a.codigo_plano} ({a.tier}) - R$ {toMoney(valorPrevio)}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>

          {form.cliente_id && resumoCliente && resumoCliente.lista.length > 0 && (
            <div className="card-resumo-cliente">
              <strong>Resumo automático do cliente</strong>
              <div style={{ marginTop: 8 }}>
                {resumoCliente.lista.map((a) => (
                  <div
                    key={a.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '4px 0',
                    }}
                  >
                    <span>
                      {a.nome_plano || a.codigo_plano} ({a.tier}){' '}
                      {a.tipo_resumo === 'pacote' ? '- pacote' : '- individual'}
                    </span>
                    <strong>R$ {toMoney(a.valor_calculado_front)}</strong>
                  </div>
                ))}
              </div>

              {!form.assinatura_id && (
                <div
                  style={{
                    marginTop: 10,
                    paddingTop: 10,
                    borderTop: '1px solid #ddd',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>Total automático do cliente</span>
                  <strong>R$ {toMoney(resumoCliente.total)}</strong>
                </div>
              )}
            </div>
          )}

          <div className="form-linha" style={{ alignItems: 'stretch' }}>
            <label style={{ display: 'flex', flexDirection: 'column' }}>
              Mês referência
              <input
                type="month"
                value={form.mes_referencia}
                onChange={(e) =>
                  setForm({ ...form, mes_referencia: e.target.value })
                }
                style={{ minHeight: 42 }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column' }}>
              Data
              <input
                type="date"
                value={form.data_pagamento}
                onChange={(e) =>
                  setForm({ ...form, data_pagamento: e.target.value })
                }
                style={{ minHeight: 42 }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column' }}>
              Valor
              <input
                value={form.valor}
                onChange={(e) =>
                  setForm({ ...form, valor: e.target.value })
                }
                style={{ minHeight: 42 }}
              />
            </label>
          </div>

          <div className="form-linha" style={{ alignItems: 'stretch' }}>
            <label style={{ display: 'flex', flexDirection: 'column' }}>
              Método
              <select
                value={form.metodo_pagamento}
                onChange={(e) =>
                  setForm({ ...form, metodo_pagamento: e.target.value })
                }
                style={{ minHeight: 42 }}
              >
                <option value="PIX">PIX</option>
                <option value="Cartão">Cartão</option>
                <option value="Transferência">Transferência</option>
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column' }}>
              Status
              <select
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value })
                }
                style={{ minHeight: 42 }}
              >
                <option value="Aguardando pagamento">Aguardando pagamento</option>
                <option value="Pago">Pago</option>
                <option value="Isento">Isento</option>
                <option value="Cancelado">Cancelado</option>
              </select>
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column' }}>
            Descrição
            <textarea
              value={form.descricao}
              onChange={(e) =>
                setForm({ ...form, descricao: e.target.value })
              }
              placeholder="Ex.: Mensalidade 04/2026 - Quantum Trades EA (starter, individual) - R$ 20.00"
              style={{ minHeight: 90, resize: 'vertical' }}
            />
          </label>

          <button type="submit" className="qt-btn qt-btn-primary">Salvar pagamento</button>
        </form>
      )}
    </div>
  );
}